import { LoaderFunction } from 'react-router-dom';
import debug from 'debug';

const logDiag = debug('rois:loaders:machinery:new-action');
if (import.meta.env.DEV) {
  debug.enable('rois:loaders:machinery:*');
}

// Types
import { decompressSessionStorage } from '../../../../../utils/sessionStorage';

// Constants
import { formActionNaming } from '../../../../../components/forms/ActionForms/constants';

// GraphQL
import {
  FETCH_AUX_MACHINE_TYPES,
  FETCH_AUX_MACHINES,
} from '../../../../../gql/queries/actions/auxMachines';
import { client } from '../../../../../gql/client';
import { FETCH_MEASURE_TYPES } from '../../../../../gql/queries/actions/measureTypes';
import { FILTER_VEHICLES } from '../../../../../gql/queries/vehicles';
import { GET_VEHICLE_CATEGORIES } from '../../../../../gql/queries/vehicleCategory';
import { GET_CONCESSIONS } from '../../../../../gql/queries/orders/orders';
import { FETCH_TEAMS } from '../../../../../gql/queries/users';

// ⚠️ SINCRONIZACIÓN: Sistema de exclusiones
import { getExcludedVehicles } from '../../../../../utils/excludedVehiclesSync';
import { claimDraftScope } from '../../../../../utils/draftOwnership';
import {
  ACTION_CREATION_SCOPE,
  ACTION_MEDIA_SCOPE,
  readCreationDraftId,
} from '../../../../../utils/draftScopes';
import { foreignCreationParentRedirect } from '../creationDraft';

export const MachineryLoader: LoaderFunction | undefined = async ({ params, request }) => {
  // Un borrador de OTRO padre no se abre bajo esta URL: atrás, una URL
  // compartida o el «Editar» de un resumen viejo enseñaban — y guardaban — el
  // borrador de la OT Y bajo la de la X (auditoría de creación 2.3).
  const foreignParent = foreignCreationParentRedirect({ params, request });
  if (foreignParent) return foreignParent;
  // Las copias de las pestañas, la lista de fotos y el croquis son del
  // borrador en curso (`draftId`): si eran de otro, fuera, antes de que nadie
  // las lea. Síncrono y antes de cualquier `await`: el loader de `details` y el
  // de la pestaña corren en paralelo (spec 2026-11-02 wizard-draft-ownership).
  const draftId = readCreationDraftId();
  claimDraftScope(ACTION_CREATION_SCOPE, draftId);
  claimDraftScope(ACTION_MEDIA_SCOPE, draftId);

  // Todo el trabajo vive aquí dentro, íntegro: las 7 consultas, el mapeo y el
  // try/catch con el respaldo offline. No se ha cambiado ni una línea de su
  // lógica — solo ha dejado de esperarse.
  const load = async () => {
    try {
  console.log('📦 [MachineryLoader] Iniciando carga del loader');

  // Stringify data - using NEW_WORK_EQUIPMENT_FORM instead of NEW_MACHINERY_FORM
  const stringifyForm = sessionStorage.getItem(
    formActionNaming.NEW_WORK_EQUIPMENT_FORM
  );

  const breadCrumbsDataStringify = sessionStorage.getItem(
    formActionNaming.NEW_ACTION_DATA
  );

  // Variables to assign
  let sessionStorageData;
  let breadCrumbsData;

  // Variables assignment
  if (stringifyForm) {
    sessionStorageData = JSON.parse(decompressSessionStorage(stringifyForm));
    console.log(
      '📦 [MachineryLoader] Cache del formulario cargado:',
      sessionStorageData
    );
  } else {
    console.log('⚠️ [MachineryLoader] No hay cache del formulario');
  }

  if (breadCrumbsDataStringify) {
    breadCrumbsData = JSON.parse(
      decompressSessionStorage(breadCrumbsDataStringify)
    );
    console.log(
      '📦 [MachineryLoader] BreadcrumbsData cargado:',
      breadCrumbsData
    );
  }

  // ✅ Cargar todos los datos en paralelo para mejor rendimiento
  const [
    {
      data: { filterVehicles: vehiclesUnsorted },
    },
    {
      data: { auxMachines: auxMachineUnsorted },
    },
    {
      data: { vehicleCategories },
    },
    {
      data: { concessions },
    },
    {
      data: { auxMachineTypes },
    },
    {
      data: { measureTypes },
    },
    {
      data: { teams },
    },
  ] = await Promise.all([
    client.query({
      query: FILTER_VEHICLES,
      variables: {
        input: {
          isActive: true,
        },
      },
      fetchPolicy: 'network-only', // ✅ Siempre traer datos frescos del servidor tras reload
    }),
    client.query({
      query: FETCH_AUX_MACHINES,
      fetchPolicy: 'network-only', // ✅ Siempre traer datos frescos del servidor tras reload
    }),
    client.query({
      query: GET_VEHICLE_CATEGORIES,
    }),
    client.query({
      query: GET_CONCESSIONS,
    }),
    client.query({
      query: FETCH_AUX_MACHINE_TYPES,
    }),
    client.query({
      query: FETCH_MEASURE_TYPES,
    }),
    client.query({
      query: FETCH_TEAMS,
      fetchPolicy: 'network-only',
    }),
  ]);

  // Build lookup maps for O(1) resolution — avoids N+1 queries (critical for offline)
  const measureTypeMap = new Map(measureTypes.map((m: { id: string; name: string }) => [m.id, m.name]));
  const auxMachineTypeMap = new Map(auxMachineTypes.map((t: { id: string; name: string }) => [t.id, t.name]));

  const auxMachinesData = auxMachineUnsorted.map(
    (machine: {
      id: string;
      auxMachineId: string;
      description: string;
      type: string;
      typeOfMeasurement: string;
      isInternal?: boolean;
      measureTypeIDs: string[];
      auxMachineTypeIDs: string[];
    }) => ({
      id: machine.id,
      auxMachineId: machine.auxMachineId,
      description: machine.description,
      type: auxMachineTypeMap.get(machine?.auxMachineTypeIDs?.[0]) || '',
      typeOfMeasurement: measureTypeMap.get(machine?.measureTypeIDs?.[0]) || '',
      isInternal: machine.isInternal !== undefined ? machine.isInternal : true,
    })
  );

  const auxMachines = [...auxMachinesData].sort((a, b) =>
    a.auxMachineId.localeCompare(b.auxMachineId)
  );

  // 🔍 [DIAG] Log raw vehicles with teamIDs to compare prod vs beta
  logDiag('vehiclesUnsorted total: %d %O', vehiclesUnsorted.length,
    vehiclesUnsorted.map((v: any) => ({ plate: v.plate, usage: v.usage, isActive: v.isActive, teamIDs: v.teamIDs || [] }))
  );

  // ✅ Construir mapa vehicleID → teams (M2M) para enriquecer vehículos con data de equipo
  const vehicleTeamMap = new Map<string, Array<{ id: string; name: string }>>();
  (teams || []).forEach((team: { id: string; name: string; vehicleIDs: string[] }) => {
    (team.vehicleIDs || []).forEach((vehicleId: string) => {
      const existing = vehicleTeamMap.get(vehicleId) || [];
      vehicleTeamMap.set(vehicleId, [...existing, { id: team.id, name: team.name }]);
    });
  });

  // ✅ NUEVO: Filtrar SOLO vehículos de construcción (Maquinaria de Obra)
  const vehicles = vehiclesUnsorted
    .filter((vehicle: any) => vehicle.usage === 'CONSTRUCTION')
    .map((vehicle: any) => {
      const vehicleTeams = vehicleTeamMap.get(vehicle.id) || [];
      return {
        id: vehicle.id,
        plate: vehicle.plate || '',
        model: vehicle.model || '',
        category: vehicle.category || '',
        ownership: vehicle.ownership, // INTERNAL o EXTERNAL
        usage: vehicle.usage, // TRANSPORT o CONSTRUCTION
        teams: vehicleTeams.map((t) => t.name),
        teamIDs: vehicleTeams.map((t) => t.id),
      };
    })
    .sort((a: any, b: any) => a.plate.localeCompare(b.plate));

  // 🔍 [DIAG] Log filtered CONSTRUCTION vehicles
  logDiag('vehicles CONSTRUCTION filtrados: %d %O', vehicles.length,
    vehicles.map((v: any) => ({ plate: v.plate, ownership: v.ownership, teams: v.teams }))
  );

  // 🔍 DEBUGGING: Extraer vehiclesTable y machineryTable del cache si existen
  const vehiclesTableFromCache = sessionStorageData?.vehiclesTable || null;
  const machineryTableFromCache = sessionStorageData?.machineryTable || null;

  // ⚠️ SINCRONIZACIÓN: Pre-cargar vehículos con conductor desde OperatorsAndVehicles
  const operatorsStorageKey = formActionNaming.NEW_OPERATORS_AND_VEHICLE_FORM;
  const operatorsDataRaw = sessionStorage.getItem(operatorsStorageKey);

  let vehiclesWithDrivers: any[] = [];
  if (operatorsDataRaw) {
    const operatorsData = JSON.parse(
      decompressSessionStorage(operatorsDataRaw)
    );
    const vehicleTable = operatorsData?.vehicleTable || {};

    // ⚠️ VALIDACIÓN 1: Solo vehículos que existen en Apollo cache
    vehiclesWithDrivers = Object.values(vehicleTable)
      .filter((v: any) => {
        // Debe tener vehicleID y driverID
        if (!v.vehicleID || !v.driverID) return false;

        // ⚠️ CRÍTICO: Verificar que el vehículo existe en la lista cargada
        return vehicles.some((vehicle: any) => vehicle.id === v.vehicleID);
      })
      .map((v: any) => {
        const fullVehicle = vehicles.find(
          (vehicle: any) => vehicle.id === v.vehicleID
        );
        return {
          id: fullVehicle.id,
          plate: fullVehicle.plate,
          model: fullVehicle.model,
          category: fullVehicle.category,
          ownership: fullVehicle.ownership,
          usage: fullVehicle.usage,
        };
      });
  }

  // ⚠️ VALIDACIÓN 2: Obtener vehículos excluidos explícitamente por el usuario
  const excludedVehicles = getExcludedVehicles(false); // false = NewAction (no validation)
  console.log(
    `📦 [MachineryLoader] Vehículos excluidos por el usuario: ${excludedVehicles.length}`,
    excludedVehicles
  );

  // ⚠️ VALIDACIÓN 3: Merge sin duplicados y sin excluidos
  // vehiclesTableFromCache debe ser un objeto Record<string, WorkVehicle>
  const vehiclesTableObject = vehiclesTableFromCache || {};
  const existingIds = new Set(
    Object.values(vehiclesTableObject).map((v: any) => v.id || v.vehicleId)
  );

  const uniqueVehiclesWithDrivers = vehiclesWithDrivers.filter((pv: any) => {
    // ⚠️ CRÍTICO: NO añadir si ya existe O si está explícitamente excluido
    return !existingIds.has(pv.id) && !excludedVehicles.includes(pv.id);
  });

  // ✅ Mantener como objeto Record<string, WorkVehicle>
  // ✅ Usar el ID del vehículo como clave directamente (sin prefijos ni timestamps)
  const mergedVehiclesTable = { ...vehiclesTableObject };
  uniqueVehiclesWithDrivers.forEach((v: any) => {
    const vehicleKey = v.id; // ✅ Simplemente el ID
    mergedVehiclesTable[vehicleKey] = {
      vehicleId: v.id,
      plate: v.plate,
      model: v.model,
      category: v.category,
      ownership: v.ownership,
      usage: v.usage,
    };
  });

  console.log(
    `📦 [MachineryLoader] Pre-carga: ${uniqueVehiclesWithDrivers.length} vehículos con conductor añadidos, ` +
      `${vehiclesWithDrivers.length - uniqueVehiclesWithDrivers.length} duplicados/excluidos evitados`
  );

  console.log(
    '📦 [MachineryLoader] VehiclesTable desde cache:',
    vehiclesTableFromCache
  );
  console.log(
    '📦 [MachineryLoader] MachineryTable desde cache:',
    machineryTableFromCache
  );
  console.log(
    '📦 [MachineryLoader] Vehículos disponibles (vehicles):',
    vehicles
  );
  console.log(
    '📦 [MachineryLoader] Máquinas disponibles (auxMachines):',
    auxMachines
  );

  const combinedData = {
    ...sessionStorageData,
    // ✅ MANTENER las tablas desde cache si existen, pero con pre-carga de vehículos con conductor
    vehiclesTable: mergedVehiclesTable,
    machineryTable: machineryTableFromCache,
    // Datos de catálogo disponibles
    vehicles,
    machines: auxMachines,
    vehicleCategories,
    concessions,
    auxMachineTypes,
    measureTypes,
    teams: (teams || []).map((t: { id: string; name: string }) => ({ id: t.id, name: t.name })),
    breadCrumbsData: {
      orderParentId: breadCrumbsData?.orderParentId,
      orderParentRoid: breadCrumbsData?.orderParentRoid,
    },
  };

  console.log('✅ [MachineryLoader] CombinedData final:', combinedData);

  return combinedData;
  } catch (err) {
    console.log('Loader offline fallback:', err);
    const breadCrumbsDataStringify = sessionStorage.getItem(formActionNaming.NEW_ACTION_DATA);
    const breadCrumbsData = breadCrumbsDataStringify ? JSON.parse(decompressSessionStorage(breadCrumbsDataStringify)) : {};
    return { vehicles: [], machines: [], vehicleCategories: [], concessions: [], auxMachineTypes: [], measureTypes: [], teams: [], vehiclesTable: {}, machineryTable: null, breadCrumbsData: { orderParentId: breadCrumbsData?.orderParentId, orderParentRoid: breadCrumbsData?.orderParentRoid } };
    }
  };

  // 🔴 SIN `await`: la promesa tiene que llegar PENDIENTE al `<Await>`. Eso es
  // lo que hace que el router termine la navegación al instante y que el
  // `<Fallback>` enseñe el Loader DENTRO de la pantalla nueva, en vez de dejar
  // al usuario mirando la anterior congelada.
  return { data: load() };
};
